import {
  Button,
  DataTable,
  EmptyState,
  Icon,
  Panel,
  Tag,
  type Column,
  type TagTone,
} from '@dayon/design-system';
import { formatCount, formatPesos } from '@dayon/money';
import { STAMP, formatPoints } from '../format.ts';
import { sentence } from '../vocabulary.ts';
import type {
  AdjustmentRow,
  BonusGrantRow,
  PlayerRecord,
  ProfileBalances,
  RestrictionRow,
} from '../types.ts';

/**
 * What this account holds, and what a payout may draw on.
 *
 * ── THE NOTICE THIS TAB USED TO CARRY, AND WHY IT IS GONE ──────────────────
 *
 * "Adjustments are not read on this screen … this profile is backed by
 * GET /api/players/:id alone, which returns the record, its restrictions, its
 * notes and its KYC submissions and nothing else." Every word of that was true
 * of the route and none of it was true of the platform:
 * `wallet.manual_adjustment` holds rows for these players and the profile read
 * simply never asked. It asks now.
 *
 * THE WARNING IN THAT NOTICE IS KEPT, because it is the part that still
 * matters: "the prototype filled this table with the three most recent
 * platform-wide adjustments; showing another player's ₱62,000 reversal under
 * this name would be worse than showing nothing." Nothing here filters a
 * platform-wide list. `GET /api/players/:id` selects
 * `WHERE a.player_id = $1`, where `$1` is the id whose player row the same
 * handler has already read through the caller's brand scope and 404'd on if it
 * was not visible. The claim is made ON THE SCREEN as well as in the SQL: the
 * notice under the table states in visible text which player reference the rows
 * are scoped to, and the table's caption — screen-reader only, as every caption
 * in this console is — names it again for anyone not reading the notice. And
 * `players-profile-money.integration.test.ts` gives two players adjustments of
 * different sizes and asserts neither ever reads the other's figure.
 *
 * ── THE THREE TILES THE NOTICE SAID WERE ABSENT ────────────────────────────
 *
 * BONUS (LOCKED) is now read. It is the balance of the player's PLAYER_BONUS
 * `wallet.account`, summed from `wallet.ledger_entry` — the same shape as
 * `wallet.player_cash_balance`, over the same table, for a different account
 * kind. There is no balance column anywhere for it to disagree with.
 *
 * POINTS is now read, as a COUNT. `points.points_ledger` earns rows for 2,012
 * players; the figure here is `sum(points)`, character for character the
 * expression behind the Points tab's own balance, so the two tabs cannot print
 * different numbers for one account. It is never formatted as money: no
 * points-to-peso rate is stored anywhere in this schema, and "worth ₱1,824"
 * would be a number this console invented.
 *
 * WITHDRAWABLE is now read — and the old notice's DEFINITION of it was wrong.
 * It said "withdrawable is cash minus locked bonus". Locked bonus is not in the
 * cash figure: `wallet.player_cash_balance` sums PLAYER_CASH only, and bonus
 * money sits in its own PLAYER_BONUS account until `convertBonusGrant` moves it
 * across. Subtracting it would deduct the same money twice and understate every
 * payout. What IS subtracted is what open withdrawal requests already claim,
 * which is exactly what `POST /api/player/withdrawals` subtracts before it
 * accepts a request and what `GET /api/player/wallet` already shows the player.
 *
 * AND IT IS NOT THE WHOLE ANSWER, so the tile never pretends to be. A
 * withdrawal block or a self-exclusion refuses the payout outright whatever the
 * arithmetic says, and when one is in force this tile prints the control's name
 * instead of a peso figure. Both come from the restrictions already on this
 * response, whose `in_force` the route computes with the same predicate the
 * release route and the request gate use — so the console cannot say a payout
 * is available that the money path is going to refuse.
 */

const ADJUSTMENT_TONE: Record<string, TagTone> = {
  AWAITING_CHECKER: 'outline',
  APPROVED: 'accent',
  REJECTED: 'quiet',
};

const adjustmentColumns: Column<AdjustmentRow>[] = [
  {
    key: 'ref',
    header: 'Reference',
    render: (a) => (
      <>
        <span className="tabular">{a.ref}</span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {STAMP.format(new Date(a.created_at))}
        </div>
      </>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    render: (a) => (
      <>
        {/* The sign is the DIRECTION, spelled out. `amount` is stored positive
            — a CHECK enforces it — so a bare figure would read the same for a
            ₱2,000 credit and a ₱2,000 debit. */}
        <span className="tabular">
          {a.direction === 'DEBIT' ? '−' : '+'}
          {formatPesos(BigInt(a.amount))}
        </span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {a.direction.toLowerCase()}
        </div>
      </>
    ),
  },
  {
    key: 'reason',
    header: 'Reason',
    render: (a) => (
      <>
        {sentence(a.reason_code)}
        {a.reason_note ? (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {a.reason_note}
          </div>
        ) : null}
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (a) => (
      <>
        <Tag tone={ADJUSTMENT_TONE[a.status] ?? 'neutral'}>{sentence(a.status)}</Tag>
        {/* An APPROVED adjustment posted a ledger transaction, and its reference
            is the thing to search the Transactions tab for. A REJECTED one moved
            nothing and carries the reason instead. */}
        {a.status === 'APPROVED' && a.transaction_ref ? (
          <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
            posted as {a.transaction_ref}
          </div>
        ) : null}
        {a.status === 'REJECTED' && a.rejected_reason ? (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {a.rejected_reason}
          </div>
        ) : null}
      </>
    ),
  },
  {
    key: 'people',
    header: 'Maker · checker',
    secondary: true,
    render: (a) => (
      <>
        <div className="text-muted">{a.maker_label ?? 'maker no longer on file'}</div>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {a.status === 'AWAITING_CHECKER'
            ? 'no checker yet — it is still in the queue'
            : (a.checker_label ?? 'checker no longer on file')}
        </div>
      </>
    ),
  },
];

/** One line of the breakdown: a figure, or the sentence that replaces it. */
function Figure({
  term,
  value,
  note,
}: {
  term: string;
  value: React.ReactNode;
  note?: React.ReactNode;
}) {
  return (
    <div className="def-row">
      <dt>{term}</dt>
      <dd>
        <div>{value}</div>
        {note ? (
          <div className="text-subtle" style={{ fontSize: '11px', textAlign: 'right' }}>
            {note}
          </div>
        ) : null}
      </dd>
    </div>
  );
}

export function BalancesTab({
  player,
  restrictions,
  balances,
  bonusGrants,
  canAdjust,
  role,
}: {
  player: PlayerRecord;
  restrictions: RestrictionRow[];
  balances: ProfileBalances;
  bonusGrants: BonusGrantRow[];
  canAdjust: boolean;
  role: string;
}) {
  const { adjustments, withdrawable } = balances;

  /*
   * The control that refuses a payout outright, if there is one.
   *
   * Self-exclusion first, which is the order `POST /api/withdrawals/:id/release`
   * itself uses — the two are different facts with different next steps, and an
   * exclusion is the terminal one.
   */
  const blocking = restrictions.find(
    (r) => r.in_force && (r.kind === 'SELF_EXCLUSION' || r.kind === 'WITHDRAWAL_BLOCK'),
  );
  const selfExcluded = restrictions.some((r) => r.in_force && r.kind === 'SELF_EXCLUSION');
  const blockingLabel = selfExcluded ? 'a self-exclusion' : blocking ? 'a withdrawal block' : null;

  const activeGrants = bonusGrants.filter((g) => g.status === 'ACTIVE');
  const unfundedActive = activeGrants.filter((g) => !g.funded);

  return (
    <>
      {/* The maker-checker rule is the reason this tab exists as a separate
          surface, so it is stated before either card. */}
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          Maker-checker: one admin raises the request, a second holding{' '}
          <span className="tabular">funds.approve</span> decides it, and nothing moves until that
          verdict. Written policy also puts a ₱50,000 tier above which the checker must be a
          Finance Lead — <em>that tier is policy with nothing behind it</em>: the platform keeps no
          such threshold anywhere, so nothing on this platform applies one —{' '}
          <strong>check it by hand</strong>. The part that IS enforced, by the platform&rsquo;s own
          records and again before anything is written, is that the maker can never be the
          checker.
        </div>
      </div>

      <div className="split">
        <Panel title="Manual adjustments on this account">
          {adjustments === null ? (
            /*
             * NOT an empty table, and not a 403 either.
             *
             * The profile itself is `players.view`; the adjustment queue, the
             * withdrawal queue and the ledger are all `transactions.view`, and
             * a Support Agent holds the first and not the second. Throwing
             * would have taken the whole page down over one panel. The key is
             * named, the way the Transactions tab already names it.
             */
            <EmptyState icon="lock-key" title="Adjustments need the ledger capability">
              This panel reads manual adjustments, which is
              gated on <span className="tabular">transactions.view</span> — the same key as the
              Transactions tab, the withdrawal queue and Player funds, because an approved
              adjustment IS a ledger movement. {role} does not hold it. This is not a statement
              about the account: rows may exist and this session may not read them.
            </EmptyState>
          ) : (
            <>
              <DataTable
                caption={`Manual adjustments raised against ${player.ref}, newest first`}
                columns={adjustmentColumns}
                rows={adjustments}
                rowKey={(a) => a.id}
                empty={
                  <EmptyState icon="receipt" title="No adjustment has been raised on this account">
                    A real zero — manual adjustments holds no
                    row for {player.ref}. Not a failed read: the whole profile is one request, and a
                    request that failed would have replaced this page with the reason rather than an
                    empty table.
                  </EmptyState>
                }
              />
              <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                <Icon name="info" />
                <div>
                  Every status, not just the queue: the checker queue shows only what is awaiting
                  a decision, and this is the full history.{' '}
                  <strong>Only {player.ref}&rsquo;s adjustments are fetched</strong>, for a player
                  already read through your brand scope — not a platform-wide list narrowed in the
                  browser. An <strong>approved</strong> adjustment also posts a ledger
                  transaction, and that row is on the Transactions tab under its own reference.
                </div>
              </div>
            </>
          )}
        </Panel>

        <Panel
          title="Balance breakdown"
          actions={
            canAdjust ? (
              <Button small disabled>
                Adjust
              </Button>
            ) : null
          }
        >
          <dl className="stack" style={{ margin: 0 }}>
            <Figure
              term="Cash"
              value={formatPesos(BigInt(player.cash_balance))}
              note="sum of the player's cash account in the ledger"
            />
            <Figure
              term="Bonus (locked)"
              value={formatPesos(BigInt(balances.bonusLocked))}
              note={
                unfundedActive.length > 0 && BigInt(balances.bonusLocked) === 0n
                  ? `${formatCount(unfundedActive.length)} active grant${
                      unfundedActive.length === 1 ? '' : 's'
                    } posted no funding movement — see Bonuses`
                  : "the player's bonus account, summed from the same ledger"
              }
            />
            <Figure
              term="Points"
              value={<span className="tabular">{formatPoints(balances.pointsBalance)}</span>}
              note="a count, not money — no rate to pesos is stored anywhere"
            />
            <Figure
              term="Available to withdraw"
              value={
                withdrawable === null ? (
                  <span className="text-subtle">not shown</span>
                ) : blockingLabel ? (
                  <span className="text-muted">Nothing — {blockingLabel} is in force</span>
                ) : (
                  formatPesos(BigInt(withdrawable.available))
                )
              }
              note={
                withdrawable === null ? (
                  <>
                    open withdrawal requests are <span className="tabular">transactions.view</span>,
                    which {role} does not hold
                  </>
                ) : blockingLabel ? (
                  selfExcluded ? (
                    'releasing this payout is refused; nobody can lift a self-exclusion early'
                  ) : (
                    'releasing this payout is refused until the block is lifted'
                  )
                ) : withdrawable.openRequests > 0 ? (
                  `cash less ${formatPesos(BigInt(withdrawable.heldInOpenRequests))} claimed by ${formatCount(
                    withdrawable.openRequests,
                  )} open request${withdrawable.openRequests === 1 ? '' : 's'}`
                ) : (
                  'no open request claims any of it'
                )
              }
            />
            <Figure
              term="Lifetime deposits"
              value={formatPesos(BigInt(player.lifetime_deposits))}
              note="settled deposits"
            />
            <Figure
              term="Lifetime withdrawals"
              value={formatPesos(BigInt(player.lifetime_withdrawals))}
              note="released withdrawals"
            />
          </dl>

          <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="info" />
            <div>
              <strong>These figures do not add up to one another, and are not meant to.</strong>{' '}
              Cash and bonus are two separate accounts,{' '}
              {/* The move is convertBonusGrant, run when wagering completes. */}
              and bonus money reaches cash only when the bonus is converted on meeting its wagering
              — so{' '}
              <strong>withdrawable is cash less open requests, not cash less bonus</strong>;
              subtracting the bonus would deduct money that was never in the cash figure. The two
              lifetime figures are settled deposits and released withdrawals, and play moves
              neither. <strong>Available to withdraw is arithmetic, not a verdict.</strong> It is
              the same figure a player is quoted when their own withdrawal is refused, but that
              check also weighs the payment method&rsquo;s own minimum and maximum and an identity
              step the player has to pass — so a payout can still be refused for a reason this
              number cannot show. Adjust is inert here rather than unbuilt: raising a credit or
              debit against this player really works, but it is maker-checker — a second admin
              holding{' '}
              <span className="tabular">funds.approve</span> decides it — so the form and the queue
              it lands in are on Player funds. <span className="tabular">funds.adjust</span> also
              carries mandatory two-factor, so a session without MFA could not raise one from this
              panel either.
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}
